// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, Divider } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartNumberInput } from "../../../components/SmartInputs";

// theme
import { B1Tiny, ModalActionsLoadingButton, ModalContentSwitch } from "../../../theme/styled";

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const DataFeedbackDefaults = {
  nInspectorState: false,
  nInspectorRefreshInterval: "1000",
  //
  nPresentTradesState: false,
  nPresentTradesRefreshInterval: "1000",
  //
  nPastTradesState: false,
  //
  nMetaDataState: false,
  //
  nTradeScreenshotsState: false,
  //
  nCurrentValueState: false,
  nCurrentValueRefreshInterval: "1000",
  nCurrentValueEvt: true,
  nCurrentValueInos: true,
  nCurrentValueCa: true,
  nCurrentValueCas: true,
  nCurrentValuePas: true,
  nCurrentValueFis: true,
  //
  nTimeAnalysisState: false,
  nTimeAnalysisRefreshInterval: "1000",
  nTimeAnalysisMinutePeriod: "10",
  nTimeAnalysisHourPeriod: "10",
  nTimeAnalysisDayPeriod: "10",
  nTimeAnalysisWeekdayPeriod: "10",
  nTimeAnalysisWeekPeriod: "10",
  nTimeAnalysisMonthPeriod: "10",
  //
  nValueAnalysisState: false,
  nValueAnalysisRefreshInterval: "1000",
  nValueAnalysisEvtPeriod: "10",
  nValueAnalysisInosPeriod: "10",
  nValueAnalysisCaPeriod: "10",
  nValueAnalysisCasPeriod: "10",
  nValueAnalysisPasPeriod: "10",
  nValueAnalysisFisPeriod: "10",
  nValueAnalysisHistogramBins: "100",
  //
  nSignalAnalysisState: false,
  nSignalAnalysisRefreshInterval: "1000",
  nSignalAnalysisBarsBefore: "50",
  nSignalAnalysisBarsAfter: "50",
  nSignalAnalysisActualTradePeriod: "100",
  nSignalAnalysisGeneralSignalPeriod: "100",
  nSignalAnalysisEvtPeriod: "100",
  nSignalAnalysisInosPeriod: "100",
  nSignalAnalysisRelioPeriod: "100",
  nSignalAnalysisCaPeriod: "100",
  nSignalAnalysisCasPeriod: "100",
  nSignalAnalysisPasPeriod: "100",
  nSignalAnalysisFisPeriod: "100",
  //
  nSymbolSeriesBarsCurrentState: false,
  nSymbolSeriesBarsCompletedState: false,
  nSymbolSeriesM1State: true,
  nSymbolSeriesM1Range: "100",
  nSymbolSeriesM5State: true,
  nSymbolSeriesM5Range: "100",
  nSymbolSeriesM15State: true,
  nSymbolSeriesM15Range: "100",
  nSymbolSeriesM30State: true,
  nSymbolSeriesM30Range: "100",
  nSymbolSeriesH1State: true,
  nSymbolSeriesH1Range: "100",
  nSymbolSeriesH4State: true,
  nSymbolSeriesH4Range: "100",
  nSymbolSeriesD1State: true,
  nSymbolSeriesD1Range: "100",
  nSymbolSeriesW1State: true,
  nSymbolSeriesW1Range: "100",
  nSymbolSeriesMNState: true,
  nSymbolSeriesMNRange: "100",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  nInspectorRefreshInterval: {
    min: 50,
    max: 86400000,
  },
  //
  nPresentTradesRefreshInterval: {
    min: 50,
    max: 86400000,
  },
  //
  nCurrentValueRefreshInterval: {
    min: 50,
    max: 86400000,
  },
  //
  nTimeAnalysisRefreshInterval: {
    min: 50,
    max: 86400000,
  },
  nTimeAnalysisMinutePeriod: {
    min: 1,
    max: 100000,
  },
  nTimeAnalysisHourPeriod: {
    min: 1,
    max: 100000,
  },
  nTimeAnalysisDayPeriod: {
    min: 1,
    max: 100000,
  },
  nTimeAnalysisWeekdayPeriod: {
    min: 1,
    max: 100000,
  },
  nTimeAnalysisWeekPeriod: {
    min: 1,
    max: 100000,
  },
  nTimeAnalysisMonthPeriod: {
    min: 1,
    max: 100000,
  },
  //
  nValueAnalysisRefreshInterval: {
    min: 50,
    max: 86400000,
  },
  nValueAnalysisEvtPeriod: {
    min: 1,
    max: 100000,
  },
  nValueAnalysisInosPeriod: {
    min: 1,
    max: 100000,
  },
  nValueAnalysisCaPeriod: {
    min: 1,
    max: 100000,
  },
  nValueAnalysisCasPeriod: {
    min: 1,
    max: 100000,
  },
  nValueAnalysisPasPeriod: {
    min: 1,
    max: 100000,
  },
  nValueAnalysisFisPeriod: {
    min: 1,
    max: 100000,
  },
  nValueAnalysisHistogramBins: {
    min: 10,
    max: 1000,
  },
  //
  nSignalAnalysisRefreshInterval: {
    min: 50,
    max: 86400000,
  },
  nSignalAnalysisBarsBefore: {
    min: 1,
    max: 10000,
  },
  nSignalAnalysisBarsAfter: {
    min: 1,
    max: 10000,
  },
  nSignalAnalysisActualTradePeriod: {
    min: 1,
    max: 100000,
  },
  nSignalAnalysisGeneralSignalPeriod: {
    min: 1,
    max: 100000,
  },
  nSignalAnalysisEvtPeriod: {
    min: 1,
    max: 100000,
  },
  nSignalAnalysisInosPeriod: {
    min: 1,
    max: 100000,
  },
  nSignalAnalysisRelioPeriod: {
    min: 1,
    max: 100000,
  },
  nSignalAnalysisCaPeriod: {
    min: 1,
    max: 100000,
  },
  nSignalAnalysisCasPeriod: {
    min: 1,
    max: 100000,
  },
  nSignalAnalysisPasPeriod: {
    min: 1,
    max: 100000,
  },
  nSignalAnalysisFisPeriod: {
    min: 1,
    max: 100000,
  },
  //
  nSymbolSeriesM1Range: {
    min: 1,
    max: 100000,
  },
  nSymbolSeriesM5Range: {
    min: 1,
    max: 100000,
  },
  nSymbolSeriesM15Range: {
    min: 1,
    max: 100000,
  },
  nSymbolSeriesM30Range: {
    min: 1,
    max: 100000,
  },
  nSymbolSeriesH1Range: {
    min: 1,
    max: 100000,
  },
  nSymbolSeriesH4Range: {
    min: 1,
    max: 100000,
  },
  nSymbolSeriesD1Range: {
    min: 1,
    max: 100000,
  },
  nSymbolSeriesW1Range: {
    min: 1,
    max: 100000,
  },
  nSymbolSeriesMNRange: {
    min: 1,
    max: 100000,
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function DataFeedbackRenderParameters(professionalMode, config) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get display values
  const getDisplayValue = (key) => {
    const value = config[key];
    if (value === "0.00000000") return "0";
    return value || "";
  };

  const getBooleanDisplay = (value) => (value === true ? "On" : "Off");

  return (
    <Stack spacing={0.5}>
      {renderParam("Inspector", getBooleanDisplay(config.nInspectorState))}
      {config.nInspectorState === true && renderParam("Inspector refresh interval", getDisplayValue("nInspectorRefreshInterval"))}

      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>

      {renderParam("Present trades", getBooleanDisplay(config.nPresentTradesState))}
      {config.nPresentTradesState === true && renderParam("Present trades refresh interval", getDisplayValue("nPresentTradesRefreshInterval"))}

      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>

      {renderParam("Past trades", getBooleanDisplay(config.nPastTradesState))}

      {professionalMode === true ? (
        <>
          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {renderParam("Meta data", getBooleanDisplay(config.nMetaDataState))}

          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {renderParam("Trade screenshots", getBooleanDisplay(config.nTradeScreenshotsState))}

          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {renderParam("Current value", getBooleanDisplay(config.nCurrentValueState))}
          {config.nCurrentValueState === true && (
            <>
              {renderParam("Current value refresh interval", getDisplayValue("nCurrentValueRefreshInterval"))}
              {renderParam("Current value news", getBooleanDisplay(config.nCurrentValueEvt))}
              {renderParam("Current value indicator", getBooleanDisplay(config.nCurrentValueInos))}
              {renderParam("Current value chart analyzer", getBooleanDisplay(config.nCurrentValueCa))}
              {renderParam("Current value Candle pattern search", getBooleanDisplay(config.nCurrentValueCas))}
              {renderParam("Current value Chart pattern search", getBooleanDisplay(config.nCurrentValuePas))}
              {renderParam("Current value Fibonacci pattern search", getBooleanDisplay(config.nCurrentValueFis))}
            </>
          )}

          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {renderParam("Time analysis", getBooleanDisplay(config.nTimeAnalysisState))}
          {config.nTimeAnalysisState === true && (
            <>
              {renderParam("Time analysis refresh interval", getDisplayValue("nTimeAnalysisRefreshInterval"))}
              {renderParam("Time analysis minute period", getDisplayValue("nTimeAnalysisMinutePeriod"))}
              {renderParam("Time analysis hour period", getDisplayValue("nTimeAnalysisHourPeriod"))}
              {renderParam("Time analysis day period", getDisplayValue("nTimeAnalysisDayPeriod"))}
              {renderParam("Time analysis weekday period", getDisplayValue("nTimeAnalysisWeekdayPeriod"))}
              {renderParam("Time analysis week period", getDisplayValue("nTimeAnalysisWeekPeriod"))}
              {renderParam("Time analysis month period", getDisplayValue("nTimeAnalysisMonthPeriod"))}
            </>
          )}

          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {renderParam("Value analysis", getBooleanDisplay(config.nValueAnalysisState))}
          {config.nValueAnalysisState === true && (
            <>
              {renderParam("Value analysis refresh interval", getDisplayValue("nValueAnalysisRefreshInterval"))}
              {renderParam("Value analysis news period", getDisplayValue("nValueAnalysisEvtPeriod"))}
              {renderParam("Value analysis indicator period", getDisplayValue("nValueAnalysisInosPeriod"))}
              {renderParam("Value analysis chart analyzer period", getDisplayValue("nValueAnalysisCaPeriod"))}
              {renderParam("Value analysis Candle pattern search period", getDisplayValue("nValueAnalysisCasPeriod"))}
              {renderParam("Value analysis Chart pattern search period", getDisplayValue("nValueAnalysisPasPeriod"))}
              {renderParam("Value analysis Fibonacci pattern search period", getDisplayValue("nValueAnalysisFisPeriod"))}
              {renderParam("Value analysis histogram bins", getDisplayValue("nValueAnalysisHistogramBins"))}
            </>
          )}

          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {renderParam("Signal analysis", getBooleanDisplay(config.nSignalAnalysisState))}
          {config.nSignalAnalysisState === true && (
            <>
              {renderParam("Signal analysis refresh interval", getDisplayValue("nSignalAnalysisRefreshInterval"))}
              {renderParam("Signal analysis bars before", getDisplayValue("nSignalAnalysisBarsBefore"))}
              {renderParam("Signal analysis bars after", getDisplayValue("nSignalAnalysisBarsAfter"))}
              {renderParam("Signal analysis actual trade period", getDisplayValue("nSignalAnalysisActualTradePeriod"))}
              {renderParam("Signal analysis general signal period", getDisplayValue("nSignalAnalysisGeneralSignalPeriod"))}
              {renderParam("Signal analysis news period", getDisplayValue("nSignalAnalysisEvtPeriod"))}
              {renderParam("Signal analysis indicator period", getDisplayValue("nSignalAnalysisInosPeriod"))}
              {renderParam("Signal analysis indicator combination period", getDisplayValue("nSignalAnalysisRelioPeriod"))}
              {renderParam("Signal analysis chart analyzer period", getDisplayValue("nSignalAnalysisCaPeriod"))}
              {renderParam("Signal analysis Candle pattern search period", getDisplayValue("nSignalAnalysisCasPeriod"))}
              {renderParam("Signal analysis Chart pattern search period", getDisplayValue("nSignalAnalysisPasPeriod"))}
              {renderParam("Signal analysis Fibonacci pattern search period", getDisplayValue("nSignalAnalysisFisPeriod"))}
            </>
          )}

          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {renderParam("Symbol series bars current", getBooleanDisplay(config.nSymbolSeriesBarsCurrentState))}
          {renderParam("Symbol series bars completed", getBooleanDisplay(config.nSymbolSeriesBarsCompletedState))}
          {config.nSymbolSeriesBarsCompletedState === true && (
            <>
              {renderParam("Symbol series M1", getBooleanDisplay(config.nSymbolSeriesM1State))}
              {config.nSymbolSeriesM1State === true && renderParam("Symbol series M1 range", getDisplayValue("nSymbolSeriesM1Range"))}
              {renderParam("Symbol series M5", getBooleanDisplay(config.nSymbolSeriesM5State))}
              {config.nSymbolSeriesM5State === true && renderParam("Symbol series M5 range", getDisplayValue("nSymbolSeriesM5Range"))}
              {renderParam("Symbol series M15", getBooleanDisplay(config.nSymbolSeriesM15State))}
              {config.nSymbolSeriesM15State === true && renderParam("Symbol series M15 range", getDisplayValue("nSymbolSeriesM15Range"))}
              {renderParam("Symbol series M30", getBooleanDisplay(config.nSymbolSeriesM30State))}
              {config.nSymbolSeriesM30State === true && renderParam("Symbol series M30 range", getDisplayValue("nSymbolSeriesM30Range"))}
              {renderParam("Symbol series H1", getBooleanDisplay(config.nSymbolSeriesH1State))}
              {config.nSymbolSeriesH1State === true && renderParam("Symbol series H1 range", getDisplayValue("nSymbolSeriesH1Range"))}
              {renderParam("Symbol series H4", getBooleanDisplay(config.nSymbolSeriesH4State))}
              {config.nSymbolSeriesH4State === true && renderParam("Symbol series H4 range", getDisplayValue("nSymbolSeriesH4Range"))}
              {renderParam("Symbol series D1", getBooleanDisplay(config.nSymbolSeriesD1State))}
              {config.nSymbolSeriesD1State === true && renderParam("Symbol series D1 range", getDisplayValue("nSymbolSeriesD1Range"))}
              {renderParam("Symbol series W1", getBooleanDisplay(config.nSymbolSeriesW1State))}
              {config.nSymbolSeriesW1State === true && renderParam("Symbol series W1 range", getDisplayValue("nSymbolSeriesW1Range"))}
              {renderParam("Symbol series MN", getBooleanDisplay(config.nSymbolSeriesMNState))}
              {config.nSymbolSeriesMNState === true && renderParam("Symbol series MN range", getDisplayValue("nSymbolSeriesMNRange"))}
            </>
          )}
        </>
      ) : null}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const DataFeedbackConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  DataFeedbackConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");
  
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Apply all the default values to the form
    Object.keys(DataFeedbackDefaults).forEach((key) => {
      setFieldValue(key, DataFeedbackDefaults[key]);
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      nInspectorState: initialData?.nInspectorState ?? DataFeedbackDefaults.nInspectorState,
      nInspectorRefreshInterval: initialData?.nInspectorRefreshInterval ?? DataFeedbackDefaults.nInspectorRefreshInterval,
      //
      nPresentTradesState: initialData?.nPresentTradesState ?? DataFeedbackDefaults.nPresentTradesState,
      nPresentTradesRefreshInterval: initialData?.nPresentTradesRefreshInterval ?? DataFeedbackDefaults.nPresentTradesRefreshInterval,
      //
      nPastTradesState: initialData?.nPastTradesState ?? DataFeedbackDefaults.nPastTradesState,
      //
      nMetaDataState: initialData?.nMetaDataState ?? DataFeedbackDefaults.nMetaDataState,
      //
      nTradeScreenshotsState: initialData?.nTradeScreenshotsState ?? DataFeedbackDefaults.nTradeScreenshotsState,
      //
      nCurrentValueState: initialData?.nCurrentValueState ?? DataFeedbackDefaults.nCurrentValueState,
      nCurrentValueRefreshInterval: initialData?.nCurrentValueRefreshInterval ?? DataFeedbackDefaults.nCurrentValueRefreshInterval,
      nCurrentValueEvt: initialData?.nCurrentValueEvt ?? DataFeedbackDefaults.nCurrentValueEvt,
      nCurrentValueInos: initialData?.nCurrentValueInos ?? DataFeedbackDefaults.nCurrentValueInos,
      nCurrentValueCa: initialData?.nCurrentValueCa ?? DataFeedbackDefaults.nCurrentValueCa,
      nCurrentValueCas: initialData?.nCurrentValueCas ?? DataFeedbackDefaults.nCurrentValueCas,
      nCurrentValuePas: initialData?.nCurrentValuePas ?? DataFeedbackDefaults.nCurrentValuePas,
      nCurrentValueFis: initialData?.nCurrentValueFis ?? DataFeedbackDefaults.nCurrentValueFis,
      //
      nTimeAnalysisState: initialData?.nTimeAnalysisState ?? DataFeedbackDefaults.nTimeAnalysisState,
      nTimeAnalysisRefreshInterval: initialData?.nTimeAnalysisRefreshInterval ?? DataFeedbackDefaults.nTimeAnalysisRefreshInterval,
      nTimeAnalysisMinutePeriod: initialData?.nTimeAnalysisMinutePeriod ?? DataFeedbackDefaults.nTimeAnalysisMinutePeriod,
      nTimeAnalysisHourPeriod: initialData?.nTimeAnalysisHourPeriod ?? DataFeedbackDefaults.nTimeAnalysisHourPeriod,
      nTimeAnalysisDayPeriod: initialData?.nTimeAnalysisDayPeriod ?? DataFeedbackDefaults.nTimeAnalysisDayPeriod,
      nTimeAnalysisWeekdayPeriod: initialData?.nTimeAnalysisWeekdayPeriod ?? DataFeedbackDefaults.nTimeAnalysisWeekdayPeriod,
      nTimeAnalysisWeekPeriod: initialData?.nTimeAnalysisWeekPeriod ?? DataFeedbackDefaults.nTimeAnalysisWeekPeriod,
      nTimeAnalysisMonthPeriod: initialData?.nTimeAnalysisMonthPeriod ?? DataFeedbackDefaults.nTimeAnalysisMonthPeriod,
      //
      nValueAnalysisState: initialData?.nValueAnalysisState ?? DataFeedbackDefaults.nValueAnalysisState,
      nValueAnalysisRefreshInterval: initialData?.nValueAnalysisRefreshInterval ?? DataFeedbackDefaults.nValueAnalysisRefreshInterval,
      nValueAnalysisEvtPeriod: initialData?.nValueAnalysisEvtPeriod ?? DataFeedbackDefaults.nValueAnalysisEvtPeriod,
      nValueAnalysisInosPeriod: initialData?.nValueAnalysisInosPeriod ?? DataFeedbackDefaults.nValueAnalysisInosPeriod,
      nValueAnalysisCaPeriod: initialData?.nValueAnalysisCaPeriod ?? DataFeedbackDefaults.nValueAnalysisCaPeriod,
      nValueAnalysisCasPeriod: initialData?.nValueAnalysisCasPeriod ?? DataFeedbackDefaults.nValueAnalysisCasPeriod,
      nValueAnalysisPasPeriod: initialData?.nValueAnalysisPasPeriod ?? DataFeedbackDefaults.nValueAnalysisPasPeriod,
      nValueAnalysisFisPeriod: initialData?.nValueAnalysisFisPeriod ?? DataFeedbackDefaults.nValueAnalysisFisPeriod,
      nValueAnalysisHistogramBins: initialData?.nValueAnalysisHistogramBins ?? DataFeedbackDefaults.nValueAnalysisHistogramBins,
      //
      nSignalAnalysisState: initialData?.nSignalAnalysisState ?? DataFeedbackDefaults.nSignalAnalysisState,
      nSignalAnalysisRefreshInterval: initialData?.nSignalAnalysisRefreshInterval ?? DataFeedbackDefaults.nSignalAnalysisRefreshInterval,
      nSignalAnalysisBarsBefore: initialData?.nSignalAnalysisBarsBefore ?? DataFeedbackDefaults.nSignalAnalysisBarsBefore,
      nSignalAnalysisBarsAfter: initialData?.nSignalAnalysisBarsAfter ?? DataFeedbackDefaults.nSignalAnalysisBarsAfter,
      nSignalAnalysisActualTradePeriod: initialData?.nSignalAnalysisActualTradePeriod ?? DataFeedbackDefaults.nSignalAnalysisActualTradePeriod,
      nSignalAnalysisGeneralSignalPeriod: initialData?.nSignalAnalysisGeneralSignalPeriod ?? DataFeedbackDefaults.nSignalAnalysisGeneralSignalPeriod,
      nSignalAnalysisEvtPeriod: initialData?.nSignalAnalysisEvtPeriod ?? DataFeedbackDefaults.nSignalAnalysisEvtPeriod,
      nSignalAnalysisInosPeriod: initialData?.nSignalAnalysisInosPeriod ?? DataFeedbackDefaults.nSignalAnalysisInosPeriod,
      nSignalAnalysisRelioPeriod: initialData?.nSignalAnalysisRelioPeriod ?? DataFeedbackDefaults.nSignalAnalysisRelioPeriod,
      nSignalAnalysisCaPeriod: initialData?.nSignalAnalysisCaPeriod ?? DataFeedbackDefaults.nSignalAnalysisCaPeriod,
      nSignalAnalysisCasPeriod: initialData?.nSignalAnalysisCasPeriod ?? DataFeedbackDefaults.nSignalAnalysisCasPeriod,
      nSignalAnalysisPasPeriod: initialData?.nSignalAnalysisPasPeriod ?? DataFeedbackDefaults.nSignalAnalysisPasPeriod,
      nSignalAnalysisFisPeriod: initialData?.nSignalAnalysisFisPeriod ?? DataFeedbackDefaults.nSignalAnalysisFisPeriod,
      //
      nSymbolSeriesBarsCurrentState: initialData?.nSymbolSeriesBarsCurrentState ?? DataFeedbackDefaults.nSymbolSeriesBarsCurrentState,
      nSymbolSeriesBarsCompletedState: initialData?.nSymbolSeriesBarsCompletedState ?? DataFeedbackDefaults.nSymbolSeriesBarsCompletedState,
      nSymbolSeriesM1State: initialData?.nSymbolSeriesM1State ?? DataFeedbackDefaults.nSymbolSeriesM1State,
      nSymbolSeriesM1Range: initialData?.nSymbolSeriesM1Range ?? DataFeedbackDefaults.nSymbolSeriesM1Range,
      nSymbolSeriesM5State: initialData?.nSymbolSeriesM5State ?? DataFeedbackDefaults.nSymbolSeriesM5State,
      nSymbolSeriesM5Range: initialData?.nSymbolSeriesM5Range ?? DataFeedbackDefaults.nSymbolSeriesM5Range,
      nSymbolSeriesM15State: initialData?.nSymbolSeriesM15State ?? DataFeedbackDefaults.nSymbolSeriesM15State,
      nSymbolSeriesM15Range: initialData?.nSymbolSeriesM15Range ?? DataFeedbackDefaults.nSymbolSeriesM15Range,
      nSymbolSeriesM30State: initialData?.nSymbolSeriesM30State ?? DataFeedbackDefaults.nSymbolSeriesM30State,
      nSymbolSeriesM30Range: initialData?.nSymbolSeriesM30Range ?? DataFeedbackDefaults.nSymbolSeriesM30Range,
      nSymbolSeriesH1State: initialData?.nSymbolSeriesH1State ?? DataFeedbackDefaults.nSymbolSeriesH1State,
      nSymbolSeriesH1Range: initialData?.nSymbolSeriesH1Range ?? DataFeedbackDefaults.nSymbolSeriesH1Range,
      nSymbolSeriesH4State: initialData?.nSymbolSeriesH4State ?? DataFeedbackDefaults.nSymbolSeriesH4State,
      nSymbolSeriesH4Range: initialData?.nSymbolSeriesH4Range ?? DataFeedbackDefaults.nSymbolSeriesH4Range,
      nSymbolSeriesD1State: initialData?.nSymbolSeriesD1State ?? DataFeedbackDefaults.nSymbolSeriesD1State,
      nSymbolSeriesD1Range: initialData?.nSymbolSeriesD1Range ?? DataFeedbackDefaults.nSymbolSeriesD1Range,
      nSymbolSeriesW1State: initialData?.nSymbolSeriesW1State ?? DataFeedbackDefaults.nSymbolSeriesW1State,
      nSymbolSeriesW1Range: initialData?.nSymbolSeriesW1Range ?? DataFeedbackDefaults.nSymbolSeriesW1Range,
      nSymbolSeriesMNState: initialData?.nSymbolSeriesMNState ?? DataFeedbackDefaults.nSymbolSeriesMNState,
      nSymbolSeriesMNRange: initialData?.nSymbolSeriesMNRange ?? DataFeedbackDefaults.nSymbolSeriesMNRange,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
    formik;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure data feedback
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure data feedback</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nInspectorState */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Inspector"
                        helpText=""
                        helpReference="HandleDataFeedback"
                        helpLink=""
                        fieldName="nInspectorState"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ my: "12px" }}
                        checked={values.nInspectorState}
                        onChange={(event) => {
                          setFieldValue("nInspectorState", event.target.checked);
                        }}
                      />
                    </Box>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nInspectorRefreshInterval */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nInspectorState === true && (
                      <Box>
                        <SmartParameter
                          value="Inspector refresh interval"
                          helpText=""
                          helpReference="HandleDataFeedback"
                          helpLink=""
                          fieldName="nInspectorRefreshInterval"
                          touched={touched}
                          errors={errors}
                        />

                        <Box sx={{ flex: 1 }}>
                          <SmartNumberInput textFieldName="nInspectorRefreshInterval" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nPresentTradesState */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Present trades"
                        helpText=""
                        helpReference="HandleDataFeedback"
                        helpLink=""
                        fieldName="nPresentTradesState"
                        touched={touched}
                        errors={errors}
                      />
                      <ModalContentSwitch
                        sx={{ my: "12px" }}
                        checked={values.nPresentTradesState}
                        onChange={(event) => {
                          setFieldValue("nPresentTradesState", event.target.checked);
                        }}
                      />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nPresentTradesRefreshInterval */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nPresentTradesState === true && (
                      <Box>
                        <SmartParameter
                          value="Present trades refresh interval"
                          helpText=""
                          helpReference="HandleDataFeedback"
                          helpLink=""
                          fieldName="nPresentTradesRefreshInterval"
                          touched={touched}
                          errors={errors}
                        />
                        <Box sx={{ flex: 1 }}>
                          <SmartNumberInput textFieldName="nPresentTradesRefreshInterval" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nPastTradesState */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Past trades"
                      helpText=""
                      helpReference="HandleDataFeedback"
                      helpLink=""
                      fieldName="nPastTradesState"
                      touched={touched}
                      errors={errors}
                    />
                    <ModalContentSwitch
                      sx={{ mt: "12px" }}
                      checked={values.nPastTradesState}
                      onChange={(event) => {
                        setFieldValue("nPastTradesState", event.target.checked);
                      }}
                    />
                  </Box>

                  {professionalMode === true ? (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nMetaDataState */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Meta data"
                          helpText=""
                          helpReference="HandleDataFeedback"
                          helpLink=""
                          fieldName="nMetaDataState"
                          touched={touched}
                          errors={errors}
                        />
                        <ModalContentSwitch
                          sx={{ mt: "12px" }}
                          checked={values.nMetaDataState}
                          onChange={(event) => {
                            setFieldValue("nMetaDataState", event.target.checked);
                          }}
                        />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nTradeScreenshotsState */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Trade screenshots"
                          helpText=""
                          helpReference="HandleDataFeedback"
                          helpLink=""
                          fieldName="nTradeScreenshotsState"
                          touched={touched}
                          errors={errors}
                        />
                        <ModalContentSwitch
                          sx={{ mt: "12px" }}
                          checked={values.nTradeScreenshotsState}
                          onChange={(event) => {
                            setFieldValue("nTradeScreenshotsState", event.target.checked);
                          }}
                        />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nCurrentValueState */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Current value"
                            helpText=""
                            helpReference="HandleDataFeedback"
                            helpLink=""
                            fieldName="nCurrentValueState"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nCurrentValueState}
                            onChange={(event) => {
                              setFieldValue("nCurrentValueState", event.target.checked);
                            }}
                          />
                        </Box>

                        {values.nCurrentValueState === true && (
                          <>
                            <Box>
                              {/* ---------------------------------------------------------------------- */}
                              {/* Parameter configuration */}
                              {/* nCurrentValueRefreshInterval */}
                              {/* ---------------------------------------------------------------------- */}
                              <SmartParameter
                                value="Current value refresh interval"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nCurrentValueRefreshInterval"
                                touched={touched}
                                errors={errors}
                              />
                              {/* ---------------------------------------------------------------------- */}
                              {/* Parameter configuration */}
                              {/* nCurrentValueRefreshInterval */}
                              {/* ---------------------------------------------------------------------- */}
                              <SmartNumberInput textFieldName="nCurrentValueRefreshInterval" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nCurrentValueEvt */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Current value news"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nCurrentValueEvt"
                                touched={touched}
                                errors={errors}
                              />
                              <ModalContentSwitch
                                sx={{ mt: "12px" }}
                                checked={values.nCurrentValueEvt}
                                onChange={(event) => {
                                  setFieldValue("nCurrentValueEvt", event.target.checked);
                                }}
                              />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nCurrentValueInos */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Current value indicator"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nCurrentValueInos"
                                touched={touched}
                                errors={errors}
                              />
                              <ModalContentSwitch
                                sx={{ mt: "12px" }}
                                checked={values.nCurrentValueInos}
                                onChange={(event) => {
                                  setFieldValue("nCurrentValueInos", event.target.checked);
                                }}
                              />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nCurrentValueCa */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Current value chart analyzer"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nCurrentValueCa"
                                touched={touched}
                                errors={errors}
                              />
                              <ModalContentSwitch
                                sx={{ mt: "12px" }}
                                checked={values.nCurrentValueCa}
                                onChange={(event) => {
                                  setFieldValue("nCurrentValueCa", event.target.checked);
                                }}
                              />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nCurrentValueCas */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Current value Candle pattern search"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nCurrentValueCas"
                                touched={touched}
                                errors={errors}
                              />
                              <ModalContentSwitch
                                sx={{ mt: "12px" }}
                                checked={values.nCurrentValueCas}
                                onChange={(event) => {
                                  setFieldValue("nCurrentValueCas", event.target.checked);
                                }}
                              />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nCurrentValuePas */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Current value Chart pattern search"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nCurrentValuePas"
                                touched={touched}
                                errors={errors}
                              />
                              <ModalContentSwitch
                                sx={{ mt: "12px" }}
                                checked={values.nCurrentValuePas}
                                onChange={(event) => {
                                  setFieldValue("nCurrentValuePas", event.target.checked);
                                }}
                              />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nCurrentValueFis */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Current value Fibonacci pattern search"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nCurrentValueFis"
                                touched={touched}
                                errors={errors}
                              />
                              <ModalContentSwitch
                                sx={{ mt: "12px" }}
                                checked={values.nCurrentValueFis}
                                onChange={(event) => {
                                  setFieldValue("nCurrentValueFis", event.target.checked);
                                }}
                              />
                            </Box>
                          </>
                        )}
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nTimeAnalysisState */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Time analysis"
                            helpText=""
                            helpReference="HandleDataFeedback"
                            helpLink=""
                            fieldName="nTimeAnalysisState"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nTimeAnalysisState}
                            onChange={(event) => {
                              setFieldValue("nTimeAnalysisState", event.target.checked);
                            }}
                          />
                        </Box>

                        {values.nTimeAnalysisState === true && (
                          <>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nTimeAnalysisRefreshInterval */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Time analysis refresh interval"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nTimeAnalysisRefreshInterval"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nTimeAnalysisRefreshInterval" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nTimeAnalysisMinutePeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Time analysis minute period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nTimeAnalysisMinutePeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nTimeAnalysisMinutePeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nTimeAnalysisHourPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Time analysis hour period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nTimeAnalysisHourPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nTimeAnalysisHourPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nTimeAnalysisDayPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Time analysis day period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nTimeAnalysisDayPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nTimeAnalysisDayPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nTimeAnalysisWeekdayPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Time analysis weekday period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nTimeAnalysisWeekdayPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nTimeAnalysisWeekdayPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nTimeAnalysisWeekPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Time analysis week period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nTimeAnalysisWeekPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nTimeAnalysisWeekPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nTimeAnalysisMonthPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Time analysis month period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nTimeAnalysisMonthPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nTimeAnalysisMonthPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          </>
                        )}
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nValueAnalysisState */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Value analysis"
                            helpText=""
                            helpReference="HandleDataFeedback"
                            helpLink=""
                            fieldName="nValueAnalysisState"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nValueAnalysisState}
                            onChange={(event) => {
                              setFieldValue("nValueAnalysisState", event.target.checked);
                            }}
                          />
                        </Box>

                        {values.nValueAnalysisState === true && (
                          <>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nValueAnalysisRefreshInterval */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Value analysis refresh interval"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nValueAnalysisRefreshInterval"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nValueAnalysisRefreshInterval" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nValueAnalysisEvtPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Value analysis news period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nValueAnalysisEvtPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nValueAnalysisEvtPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nValueAnalysisInosPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Value analysis indicator period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nValueAnalysisInosPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nValueAnalysisInosPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nValueAnalysisCaPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Value analysis chart analyzer period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nValueAnalysisCaPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nValueAnalysisCaPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nValueAnalysisCasPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Value analysis Candle pattern search period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nValueAnalysisCasPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nValueAnalysisCasPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nValueAnalysisPasPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Value analysis Chart pattern search period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nValueAnalysisPasPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nValueAnalysisPasPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nValueAnalysisFisPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Value analysis Fibonacci pattern search period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nValueAnalysisFisPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nValueAnalysisFisPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          </>
                        )}
                      </Box>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nValueAnalysisHistogramBins */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nValueAnalysisState === true && (
                        <Box>
                          <SmartParameter
                            value="Value analysis histogram bins"
                            helpText=""
                            helpReference="HandleDataFeedback"
                            helpLink=""
                            fieldName="nValueAnalysisHistogramBins"
                            touched={touched}
                            errors={errors}
                          />
                          <SmartNumberInput textFieldName="nValueAnalysisHistogramBins" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                        </Box>
                      )}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nSignalAnalysisState */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Signal analysis"
                            helpText=""
                            helpReference="HandleDataFeedback"
                            helpLink=""
                            fieldName="nSignalAnalysisState"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nSignalAnalysisState}
                            onChange={(event) => {
                              setFieldValue("nSignalAnalysisState", event.target.checked);
                            }}
                          />
                        </Box>

                        {values.nSignalAnalysisState === true && (
                          <>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisRefreshInterval */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis refresh interval"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisRefreshInterval"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisRefreshInterval" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisBarsBefore */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis bars before"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisBarsBefore"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisBarsBefore" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisBarsAfter */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis bars after"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisBarsAfter"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisBarsAfter" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisActualTradePeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis actual trade period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisActualTradePeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisActualTradePeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisGeneralSignalPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis general signal period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisGeneralSignalPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisGeneralSignalPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisEvtPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis news period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisEvtPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisEvtPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisInosPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis indicator period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisInosPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisInosPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisRelioPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis indicator combination period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisRelioPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisRelioPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisCaPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis chart analyzer period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisCaPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisCaPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisCasPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis Candle pattern search period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisCasPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisCasPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisPasPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis Chart pattern search period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisPasPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisPasPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* nSignalAnalysisFisPeriod */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Signal analysis Fibonacci pattern search period"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSignalAnalysisFisPeriod"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSignalAnalysisFisPeriod" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          </>
                        )}
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nSymbolSeriesBarsCurrentState */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Symbol series bars current"
                            helpText=""
                            helpReference="HandleDataFeedback"
                            helpLink=""
                            fieldName="nSymbolSeriesBarsCurrentState"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nSymbolSeriesBarsCurrentState}
                            onChange={(event) => {
                              setFieldValue("nSymbolSeriesBarsCurrentState", event.target.checked);
                            }}
                          />
                        </Box>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nSymbolSeriesBarsCompletedState */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Symbol series bars completed"
                            helpText=""
                            helpReference="HandleDataFeedback"
                            helpLink=""
                            fieldName="nSymbolSeriesBarsCompletedState"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nSymbolSeriesBarsCompletedState}
                            onChange={(event) => {
                              setFieldValue("nSymbolSeriesBarsCompletedState", event.target.checked);
                            }}
                          />
                        </Box>
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nSymbolSeriesBarsCompletedState === true ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesM1State */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Symbol series M1"
                              helpText=""
                              helpReference="HandleDataFeedback"
                              helpLink=""
                              fieldName="nSymbolSeriesM1State"
                              touched={touched}
                              errors={errors}
                            />
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.nSymbolSeriesM1State}
                              onChange={(event) => {
                                setFieldValue("nSymbolSeriesM1State", event.target.checked);
                              }}
                            />
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesM1Range */}
                          {/* ---------------------------------------------------------------------- */}
                          {values.nSymbolSeriesM1State === true && (
                            <Box>
                              <SmartParameter
                                value="Symbol series M1 range"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSymbolSeriesM1Range"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSymbolSeriesM1Range" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          )}
                        </Box>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nSymbolSeriesBarsCompletedState === true ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesM5State */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Symbol series M5"
                              helpText=""
                              helpReference="HandleDataFeedback"
                              helpLink=""
                              fieldName="nSymbolSeriesM5State"
                              touched={touched}
                              errors={errors}
                            />
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.nSymbolSeriesM5State}
                              onChange={(event) => {
                                setFieldValue("nSymbolSeriesM5State", event.target.checked);
                              }}
                            />
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesM5Range */}
                          {/* ---------------------------------------------------------------------- */}
                          {values.nSymbolSeriesM5State === true && (
                            <Box>
                              <SmartParameter
                                value="Symbol series M5 range"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSymbolSeriesM5Range"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSymbolSeriesM5Range" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          )}
                        </Box>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nSymbolSeriesBarsCompletedState === true ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesM15State */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Symbol series M15"
                              helpText=""
                              helpReference="HandleDataFeedback"
                              helpLink=""
                              fieldName="nSymbolSeriesM15State"
                              touched={touched}
                              errors={errors}
                            />
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.nSymbolSeriesM15State}
                              onChange={(event) => {
                                setFieldValue("nSymbolSeriesM15State", event.target.checked);
                              }}
                            />
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesM15Range */}
                          {/* ---------------------------------------------------------------------- */}
                          {values.nSymbolSeriesM15State === true && (
                            <Box>
                              <SmartParameter
                                value="Symbol series M15 range"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSymbolSeriesM15Range"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSymbolSeriesM15Range" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          )}
                        </Box>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nSymbolSeriesBarsCompletedState === true ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesM30State */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Symbol series M30"
                              helpText=""
                              helpReference="HandleDataFeedback"
                              helpLink=""
                              fieldName="nSymbolSeriesM30State"
                              touched={touched}
                              errors={errors}
                            />
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.nSymbolSeriesM30State}
                              onChange={(event) => {
                                setFieldValue("nSymbolSeriesM30State", event.target.checked);
                              }}
                            />
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesM30Range */}
                          {/* ---------------------------------------------------------------------- */}
                          {values.nSymbolSeriesM30State === true && (
                            <Box>
                              <SmartParameter
                                value="Symbol series M30 range"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSymbolSeriesM30Range"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSymbolSeriesM30Range" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          )}
                        </Box>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nSymbolSeriesBarsCompletedState === true ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesH1State */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Symbol series H1"
                              helpText=""
                              helpReference="HandleDataFeedback"
                              helpLink=""
                              fieldName="nSymbolSeriesH1State"
                              touched={touched}
                              errors={errors}
                            />
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.nSymbolSeriesH1State}
                              onChange={(event) => {
                                setFieldValue("nSymbolSeriesH1State", event.target.checked);
                              }}
                            />
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesH1Range */}
                          {/* ---------------------------------------------------------------------- */}
                          {values.nSymbolSeriesH1State === true && (
                            <Box>
                              <SmartParameter
                                value="Symbol series H1 range"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSymbolSeriesH1Range"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSymbolSeriesH1Range" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          )}
                        </Box>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nSymbolSeriesBarsCompletedState === true ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesH4State */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Symbol series H4"
                              helpText=""
                              helpReference="HandleDataFeedback"
                              helpLink=""
                              fieldName="nSymbolSeriesH4State"
                              touched={touched}
                              errors={errors}
                            />
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.nSymbolSeriesH4State}
                              onChange={(event) => {
                                setFieldValue("nSymbolSeriesH4State", event.target.checked);
                              }}
                            />
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesH4Range */}
                          {/* ---------------------------------------------------------------------- */}
                          {values.nSymbolSeriesH4State === true && (
                            <Box>
                              <SmartParameter
                                value="Symbol series H4 range"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSymbolSeriesH4Range"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSymbolSeriesH4Range" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          )}
                        </Box>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nSymbolSeriesBarsCompletedState === true ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesD1State */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Symbol series D1"
                              helpText=""
                              helpReference="HandleDataFeedback"
                              helpLink=""
                              fieldName="nSymbolSeriesD1State"
                              touched={touched}
                              errors={errors}
                            />
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.nSymbolSeriesD1State}
                              onChange={(event) => {
                                setFieldValue("nSymbolSeriesD1State", event.target.checked);
                              }}
                            />
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesD1Range */}
                          {/* ---------------------------------------------------------------------- */}
                          {values.nSymbolSeriesD1State === true && (
                            <Box>
                              <SmartParameter
                                value="Symbol series D1 range"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSymbolSeriesD1Range"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSymbolSeriesD1Range" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          )}
                        </Box>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nSymbolSeriesBarsCompletedState === true ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesW1State */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Symbol series W1"
                              helpText=""
                              helpReference="HandleDataFeedback"
                              helpLink=""
                              fieldName="nSymbolSeriesW1State"
                              touched={touched}
                              errors={errors}
                            />
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.nSymbolSeriesW1State}
                              onChange={(event) => {
                                setFieldValue("nSymbolSeriesW1State", event.target.checked);
                              }}
                            />
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesW1Range */}
                          {/* ---------------------------------------------------------------------- */}
                          {values.nSymbolSeriesW1State === true && (
                            <Box>
                              <SmartParameter
                                value="Symbol series W1 range"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSymbolSeriesW1Range"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSymbolSeriesW1Range" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          )}
                        </Box>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      {values.nSymbolSeriesBarsCompletedState === true ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesMNState */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <SmartParameter
                              value="Symbol series MN"
                              helpText=""
                              helpReference="HandleDataFeedback"
                              helpLink=""
                              fieldName="nSymbolSeriesMNState"
                              touched={touched}
                              errors={errors}
                            />
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.nSymbolSeriesMNState}
                              onChange={(event) => {
                                setFieldValue("nSymbolSeriesMNState", event.target.checked);
                              }}
                            />
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Parameter configuration */}
                          {/* nSymbolSeriesMNRange */}
                          {/* ---------------------------------------------------------------------- */}
                          {values.nSymbolSeriesMNState === true && (
                            <Box>
                              <SmartParameter
                                value="Symbol series MN range"
                                helpText=""
                                helpReference="HandleDataFeedback"
                                helpLink=""
                                fieldName="nSymbolSeriesMNRange"
                                touched={touched}
                                errors={errors}
                              />
                              <SmartNumberInput textFieldName="nSymbolSeriesMNRange" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                            </Box>
                          )}
                        </Box>
                      ) : null}
                    </>
                  ) : null}
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
